/**
 * Legal modal — displays PRIVACY.md or TERMS.md as a scrollable overlay.
 *
 * Usage:
 *   openLegalModal('privacy');   // opens docs/PRIVACY.md
 *   openLegalModal('terms');     // opens docs/TERMS.md
 *
 * The modal is appended to document.body and removes itself on close.
 * Markdown headings/bold/tables are rendered with minimal DOM parsing
 * (no external markdown library dependency).
 */

type LegalDoc = 'privacy' | 'terms';

const DOC_PATHS: Record<LegalDoc, string> = {
  privacy: '/docs/PRIVACY.md',
  terms: '/docs/TERMS.md',
};

const DOC_TITLES: Record<LegalDoc, string> = {
  privacy: '隱私政策',
  terms: '使用條款',
};

/** Minimal markdown → HTML converter for headings, bold, tables, hr, lists. */
function mdToHtml(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  let inTable = false;
  let tableHeaderDone = false;
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();

    // Heading
    const h3 = line.match(/^### (.+)/);
    if (h3) {
      if (inList) { out.push('</ul>'); inList = false; }
      if (inTable) { out.push('</tbody></table>'); inTable = false; tableHeaderDone = false; }
      out.push(`<h3>${inlineFormat(h3[1])}</h3>`);
      continue;
    }
    const h2 = line.match(/^## (.+)/);
    if (h2) {
      if (inList) { out.push('</ul>'); inList = false; }
      if (inTable) { out.push('</tbody></table>'); inTable = false; tableHeaderDone = false; }
      out.push(`<h2>${inlineFormat(h2[1])}</h2>`);
      continue;
    }
    const h1 = line.match(/^# (.+)/);
    if (h1) {
      if (inList) { out.push('</ul>'); inList = false; }
      if (inTable) { out.push('</tbody></table>'); inTable = false; tableHeaderDone = false; }
      out.push(`<h1>${inlineFormat(h1[1])}</h1>`);
      continue;
    }

    // Blockquote
    const bq = line.match(/^> (.+)/);
    if (bq) {
      if (inList) { out.push('</ul>'); inList = false; }
      if (inTable) { out.push('</tbody></table>'); inTable = false; tableHeaderDone = false; }
      out.push(`<blockquote>${inlineFormat(bq[1])}</blockquote>`);
      continue;
    }

    // HR
    if (/^---+$/.test(line.trim())) {
      if (inList) { out.push('</ul>'); inList = false; }
      if (inTable) { out.push('</tbody></table>'); inTable = false; tableHeaderDone = false; }
      out.push('<hr>');
      continue;
    }

    // Table row (pipe-delimited)
    if (line.startsWith('|')) {
      if (inList) { out.push('</ul>'); inList = false; }
      const cells = line.split('|').slice(1, -1).map(c => c.trim());
      // Separator row
      if (cells.every(c => /^[-:]+$/.test(c))) {
        tableHeaderDone = true;
        continue;
      }
      if (!inTable) {
        out.push('<table><thead><tr>');
        cells.forEach(c => out.push(`<th>${inlineFormat(c)}</th>`));
        out.push('</tr></thead><tbody>');
        inTable = true;
        tableHeaderDone = false;
      } else if (!tableHeaderDone) {
        // Extra header rows before separator
        out.push('<tr>');
        cells.forEach(c => out.push(`<th>${inlineFormat(c)}</th>`));
        out.push('</tr>');
      } else {
        out.push('<tr>');
        cells.forEach(c => out.push(`<td>${inlineFormat(c)}</td>`));
        out.push('</tr>');
      }
      continue;
    }

    // End table if we reach a non-table line
    if (inTable) {
      out.push('</tbody></table>');
      inTable = false;
      tableHeaderDone = false;
    }

    // List item
    const li = line.match(/^- (.+)/);
    if (li) {
      if (!inList) { out.push('<ul>'); inList = true; }
      out.push(`<li>${inlineFormat(li[1])}</li>`);
      continue;
    }

    // End list if we reach a non-list line
    if (inList && line.trim() !== '') {
      out.push('</ul>');
      inList = false;
    }

    // Empty line → paragraph break
    if (line.trim() === '') {
      if (inList) { out.push('</ul>'); inList = false; }
      out.push('<br>');
      continue;
    }

    // Normal paragraph line
    out.push(`<p>${inlineFormat(line)}</p>`);
  }

  if (inList) out.push('</ul>');
  if (inTable) out.push('</tbody></table>');

  return out.join('\n');
}

/** Apply bold (**text**), code (`text`), links ([text](url)) inline. */
function inlineFormat(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
}

export function openLegalModal(doc: LegalDoc): void {
  // Prevent duplicate modals
  const existing = document.getElementById('legal-modal-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'legal-modal-overlay';
  overlay.className = 'legal-modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', DOC_TITLES[doc]);

  overlay.innerHTML = `
    <div class="legal-modal">
      <div class="legal-modal-header">
        <h2 class="legal-modal-title">📄 ${DOC_TITLES[doc]}</h2>
        <button class="legal-modal-close" aria-label="關閉">✕</button>
      </div>
      <div class="legal-modal-body">
        <div class="legal-modal-loading">載入中…</div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  // Close handlers
  const closeBtn = overlay.querySelector('.legal-modal-close') as HTMLButtonElement;
  closeBtn.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  document.addEventListener('keydown', function escHandler(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      overlay.remove();
      document.removeEventListener('keydown', escHandler);
    }
  });

  // Fetch and render
  const body = overlay.querySelector('.legal-modal-body') as HTMLElement;
  fetch(DOC_PATHS[doc])
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    })
    .then(md => {
      body.innerHTML = `<div class="legal-modal-content">${mdToHtml(md)}</div>`;
    })
    .catch(() => {
      body.innerHTML = `
        <div class="legal-modal-error">
          <p>無法載入文件，請稍後再試。</p>
          <p>或直接寄信：<a href="mailto:support@yongheng.tw">support@yongheng.tw</a></p>
        </div>
      `;
    });
}
